/**
 * Breadcrumbs Component
 *
 * Navigation breadcrumbs for showing current location in the app hierarchy.
 *
 * Usage:
 * <Breadcrumbs>
 *   <BreadcrumbItem href="/">Home</BreadcrumbItem>
 *   <BreadcrumbItem href="/catalog">Catalog</BreadcrumbItem>
 *   <BreadcrumbItem>RUPE API</BreadcrumbItem>
 * </Breadcrumbs>
 */

import React, { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';

interface BreadcrumbsProps {
  children: ReactNode;
  className?: string;
}

export const Breadcrumbs: React.FC<BreadcrumbsProps> = ({ children, className = '' }) => {
  return (
    <nav aria-label="Breadcrumb" className={`breadcrumbs ${className}`}>
      <ol className="flex items-center gap-2 text-sm">
        {React.Children.map(children, (child, index) => {
          const isLast = index === React.Children.count(children) - 1;
          return (
            <>
              {child}
              {!isLast && (
                <li aria-hidden="true" className="text-slate-400 dark:text-slate-500">
                  <ChevronRight className="w-4 h-4" />
                </li>
              )}
            </>
          );
        })}
      </ol>
    </nav>
  );
};

interface BreadcrumbItemProps {
  href?: string;
  children: ReactNode;
  icon?: ReactNode;
  className?: string;
}

export const BreadcrumbItem: React.FC<BreadcrumbItemProps> = ({
  href,
  children,
  icon,
  className = '',
}) => {
  const isLink = !!href;

  const content = (
    <span className="flex items-center gap-1.5">
      {icon && <span className="flex-shrink-0">{icon}</span>}
      <span>{children}</span>
    </span>
  );

  if (isLink) {
    return (
      <li className={className}>
        <Link
          to={href}
          className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
          style={{
            fontFamily: 'var(--font-sans)',
            fontSize: 'var(--text-sm)',
          }}
        >
          {content}
        </Link>
      </li>
    );
  }

  return (
    <li
      className={`text-gray-900 dark:text-gray-200 font-medium ${className}`}
      aria-current="page"
      style={{
        fontFamily: 'var(--font-sans)',
        fontSize: 'var(--text-sm)',
        fontWeight: 'var(--font-medium)',
      }}
    >
      {content}
    </li>
  );
};
